import { useEffect, useState } from "react";
import { Linkedin, Mail, Compass, GraduationCap, Briefcase, Lightbulb, Rocket, Quote, CheckCircle2, Star } from "lucide-react";
import api from "@/lib/api";

const TIMELINE = [
  { icon: GraduationCap, text: "Bachelor's Degree in Project Management" },
  { icon: Briefcase, text: "Operations & Leadership" },
  { icon: Lightbulb, text: "Founded PM LaunchPad AI" },
  { icon: Rocket, text: "Building the future of AI-powered PM training" },
];
const COMMUNITY = ["Students", "Career Changers", "Project Managers", "Recruiters", "Universities", "Employers"];
const BADGES = ["Founder Built", "Community Driven", "AI Powered", "Built in Public", "Continuously Improving"];
const LINKEDIN = "https://www.linkedin.com/in/nehemiah-harris";

export default function MeetFounder() {
  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => { (async () => {
    try { const r = await api.get("/testimonials/featured"); setTestimonials(r.data || []); } catch (e) {}
  })(); }, []);
  return (
    <section data-testid="meet-founder" className="relative z-20 max-w-[1400px] mx-auto px-4 md:px-8 pb-28">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
        <div className="grid lg:grid-cols-[320px_1fr]">
          {/* Left — identity */}
          <div className="p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-white/10 flex flex-col items-center text-center">
            <div className="text-[11px] uppercase tracking-[2px] text-volt mb-5">Meet the Founder</div>
            <img src="/assets/founder.jpeg" alt="Nehemiah Harris" data-testid="founder-photo"
              className="w-36 h-36 rounded-2xl object-cover border border-white/15 shadow-xl" />
            <h3 className="font-heading font-extrabold text-xl mt-5 text-white">Nehemiah Harris</h3>
            <p className="text-volt text-sm font-medium mt-1">Founder & Creator</p>
            <p className="text-white/50 text-xs">PM LaunchPad AI</p>
            <div className="flex flex-col gap-2 w-full mt-6">
              <a href={LINKEDIN} target="_blank" rel="noopener noreferrer" data-testid="founder-linkedin"
                className="flex items-center justify-center gap-2 rounded-full bg-[#0A66C2] hover:bg-[#0A66C2]/90 text-white text-sm font-medium py-2.5 transition-colors duration-200">
                <Linkedin className="w-4 h-4" /> Connect on LinkedIn
              </a>
              <a href="mailto:founder@pmlaunchpadai.com?subject=Hello%20from%20PM%20LaunchPad" data-testid="founder-contact"
                className="flex items-center justify-center gap-2 rounded-full border border-white/15 hover:bg-white/5 text-white text-sm font-medium py-2.5 transition-colors duration-200">
                <Mail className="w-4 h-4" /> Contact Founder
              </a>
              <span className="flex items-center justify-center gap-2 rounded-full border border-white/10 text-white/40 text-xs py-2">
                <Compass className="w-3.5 h-3.5" /> Follow the Journey · coming soon
              </span>
            </div>
          </div>

          {/* Right — story */}
          <div className="p-8 md:p-10">
            <div className="space-y-4 text-white/70 leading-relaxed text-[15px]">
              <p>Hi, I'm Nehemiah. After earning my Bachelor's degree in Project Management and building a career in operations and leadership, I kept seeing talented people hear the same thing:</p>
              <p className="text-white/90 font-medium">"You need experience before we can hire you."</p>
              <p>I believed there had to be a better way. That's why I built PM LaunchPad AI. My mission is to help aspiring project managers gain meaningful workplace experience before receiving their first opportunity.</p>
              <p className="text-white/90">This platform isn't designed to replace experience — it's designed to help people <span className="text-volt">earn</span> it.</p>
            </div>

            <blockquote className="relative mt-7 rounded-xl border border-volt/30 bg-volt/[0.06] p-5">
              <Quote className="w-5 h-5 text-volt mb-2" />
              <p className="text-white font-heading font-semibold text-lg leading-snug">"Experience builds confidence. Confidence creates opportunity. PM LaunchPad AI exists to help people earn both."</p>
              <footer className="text-white/50 text-sm mt-2">— Nehemiah Harris</footer>
            </blockquote>

            {/* Mission */}
            <div className="mt-7">
              <p className="text-[11px] uppercase tracking-[2px] text-white/40 mb-2">Why PM LaunchPad AI Exists</p>
              <p className="text-white/70 text-sm leading-relaxed">Every project manager deserves a first opportunity. PM LaunchPad AI bridges the gap between learning and employment through realistic, AI-powered workplace simulations.</p>
              <p className="inline-block mt-3 rounded-full bg-white/5 border border-white/10 px-4 py-1.5 text-sm font-semibold text-volt">Mission: Experience Before Opportunity</p>
            </div>

            {/* Timeline */}
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {TIMELINE.map((t, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-3">
                  <span className="w-9 h-9 grid place-items-center rounded-full bg-volt/15 text-volt shrink-0"><t.icon className="w-4 h-4" /></span>
                  <span className="text-sm text-white/80">{t.text}</span>
                </div>
              ))}
            </div>

            {/* Community */}
            <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.02] p-5">
              <p className="text-white font-heading font-bold text-sm">Built with the community</p>
              <p className="text-white/60 text-sm mt-1">PM LaunchPad AI is continuously improving through feedback from:</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {COMMUNITY.map((c) => <span key={c} className="text-xs rounded-full border border-white/10 bg-white/5 text-white/70 px-3 py-1">{c}</span>)}
              </div>
              <p className="text-white/70 text-sm mt-4 leading-relaxed">You are one of our earliest beta users. Your feedback is reviewed directly by the founder and influences future improvements. <span className="text-volt">Thank you for helping build something meaningful.</span></p>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-2 mt-6">
              {BADGES.map((b) => (
                <span key={b} className="flex items-center gap-1.5 text-xs text-white/70"><CheckCircle2 className="w-3.5 h-3.5 text-volt" /> {b}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {testimonials.length > 0 && (
        <div className="mt-8" data-testid="founder-testimonials">
          <p className="text-center text-[11px] uppercase tracking-[2px] text-white/40 mb-5">What beta testers say</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {testimonials.map((t, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
                {t.rating ? <div className="flex gap-0.5 mb-2">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-3.5 h-3.5 ${n <= t.rating ? "fill-volt text-volt" : "text-white/20"}`} />)}</div> : null}
                <p className="text-white/80 text-sm leading-relaxed italic">"{t.quote}"</p>
                <p className="text-white/50 text-xs mt-3 font-mono">— {t.name}{t.role ? `, ${t.role}` : ""}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
